import type { Messages } from './en';

/**
 * 階段 B/C 世界：可以拜訪的家、夜市、節慶和旅行（繁體中文・台灣）。
 * 標示「英文練習」的句子在每個語言裡都刻意保留英文。
 */
export const zhTWWorld3: Pick<Messages, 'world3'> = {
  world3: {
    mapNames: { grandma: '李奶奶家', bo: '小寶家', market: '燈籠夜市' },
    zones: { grandma: '李奶奶家', bo: '小寶家', market: '夜市', marketGames: '遊戲攤' },
    objects: {
      grTea: '茶桌',
      grCooker: '老電鍋',
      grPhotos: '全家福照片',
      grSewing: '縫紉機',
      grCalendar: '撕日曆',
      boConsole: '遊戲機',
      boAquarium: '水族箱',
      boComics: '漫畫書櫃',
      boLego: '積木',
      mkTea: '珍珠奶茶攤',
      mkChicken: '炸雞排攤',
      mkBalls: '地瓜球',
      mkTofu: '臭豆腐攤',
      mkOyster: '蚵仔煎攤',
      mkIce: '芒果冰攤',
      mkFish: '撈金魚',
      mkRings: '套圈圈',
      mkClaw: '夾娃娃機',
      marketGate: '夜市入口',
      fsRiddles: '猜燈謎',
      fsTangyuan: '湯圓攤車',
      fsBoat: '龍舟',
      fsZongzi: '包粽子桌',
      fsEgg: '立蛋桌',
      fsBBQ: '中秋烤肉',
      fsPomelo: '柚子籃',
      fsLion: '舞獅',
      fsReunion: '年夜飯',
    },
    npc: { boMom: '小寶媽媽', teaVendor: '花阿姨', chickenVendor: '康叔叔', gameVendor: '老樓叔叔', shopper1: '逛夜市的人', shopper2: '逛夜市的人', shopper3: '逛夜市的人' },

    closed: {
      grandma: '沒有人應門。李奶奶通常晚上六點以後才會在家。',
      bo: '家裡沒有人。小寶一家傍晚才會回來。',
      market: '夜市下午五點才開喔。晚上再來吧！',
    },

    grandma: {
      hello: '進來、進來！鞋子脫一脫，坐下來。要不要喝杯茶？',
      teaText: '奶奶把烏龍茶倒進小小的茶杯裡。「先聞聞看，再慢慢喝。」',
      drink: '🍵 跟奶奶一起喝茶',
      teaDone: '暖暖的，好香。奶奶跟你說起她從小長大的茶山。',
      teaWords: '英文練習：“This tea smells lovely. Thank you, Grandma!”',
      cooker: '那台有名的紅色大同電鍋。奶奶說它比你媽媽年紀還大——到現在蒸出來的包子還是最好吃！',
      steam: '🥟 一起蒸包子',
      steamDone: '鬆鬆軟軟的肉包！奶奶包了兩個讓你帶回家給家人。',
      photos: '黑白老照片：小時候在農場的奶奶、一場婚禮、一個包在紅毯子裡的小寶寶……',
      readStory: '📖 聽奶奶說故事',
      sewing: '一台腳踩的老縫紉機。喀噠喀噠喀噠！',
      sewLearn: '🧵 學縫釦子',
      sewDone: '你縫好了人生第一顆釦子！奶奶送你一條她親手做的小圍巾。',
      calendar: (date: string) => `一本厚厚的撕日曆。今天這一頁：${date}。上面還寫著哪幾天是好日子喔！`,
      tearPage: '📅 撕下今天這一頁',
      tearDone: '唰——！奶奶笑著說：「這是一天裡最開心的時刻。」',
      noGrandma: '李奶奶現在不在家。',
      mochi: '小貓麻糬縮在籃子裡，呼嚕呼嚕地睡著。',
      byeGift: '你要走之前，奶奶塞給你一袋橘子。「拿回去給家人吃！」',
    },
    bo: {
      momHello: '哎呀，是小寶的朋友！謝謝你幫他拿回風箏。要不要吃水餃？',
      momDumplings: '🥟 好呀，謝謝！（用英文說：“Yes, please. They smell great!”）',
      momDone: '多汁的水餃沾一點醋。小寶媽媽笑著說：「要常常來玩喔！」',
      console: '小寶的遊戲機。「我們一起玩吧！」',
      playConsole: '🎮 玩一場遊戲',
      aquarium: '一群霓虹燈魚和一隻懶洋洋的小烏龜。小寶幫烏龜取名叫「火箭」。',
      feedFish: '🐟 餵魚',
      fed: '魚兒一下子衝上來搶飼料。火箭烏龜一點都不急。',
      comics: '小寶的漫畫書，有中文的也有英文的。有幾頁旁邊還有塗鴉。',
      readComic: '📖 看漫畫',
      lego: '一盒積木，還有一座蓋到一半的太空站。',
      build: '🧱 跟小寶一起蓋',
      built: '你和小寶把太空站蓋好了。「三、二、一……發射！」',
      boAway: '小寶現在不在家。',
    },

    fest: {
      names: { lantern: '元宵節', dragonBoat: '端午節', moon: '中秋節', newYear: '農曆新年' },
      announce: (name: string) => `🎉 這個禮拜是${name}！快去公園一起過節吧。`,
      riddles: {
        intro: '到處掛著寫了謎語的紅燈籠。猜對就能得到小獎品！（燈謎是英文的喔。）',
        guess: '🏮 猜一個燈謎',
        right: '🎉 答對了！顧燈籠的伯伯送你一張貼紙。',
        wrong: '這次沒猜中——再讀一次看看！',
        allDone: '今天的燈謎你全部猜完了。明天再來吧！',
        list: [
          { q: 'I have hands but I can’t clap. What am I?', a: ['A clock', 'A tree', 'A shoe'] },
          { q: 'The more you take, the more you leave behind. What am I?', a: ['Footsteps', 'Cookies', 'Money'] },
          { q: 'I’m tall when I’m young and short when I’m old. What am I?', a: ['A candle', 'A dog', 'A book'] },
          { q: 'What has keys but can’t open any door?', a: ['A piano', 'A car', 'A teacher'] },
          { q: 'What goes up but never comes down?', a: ['Your age', 'A ball', 'Rain'] },
          { q: 'What has a neck but no head?', a: ['A bottle', 'A snake', 'A chair'] },
          { q: 'I’m full of holes, but I can still hold water. What am I?', a: ['A sponge', 'A cup', 'A net'] },
          { q: 'What gets wetter the more it dries?', a: ['A towel', 'The sun', 'A fish'] },
          { q: 'What can you catch but not throw?', a: ['A cold', 'A ball', 'A bus'] },
        ],
      },
      tangyuan: '熱呼呼的湯圓——泡在薑湯裡的甜甜糯米球。圓圓的像滿月，代表一家人團團圓圓。',
      eatTangyuan: '🥣 吃一碗',
      tangyuanDone: 'Q彈又香甜，裡面是黑芝麻餡。肚子暖洋洋的！',
      boat: '兩艘長長的龍舟在池塘上搖來搖去。打鼓的人揮揮手：「要不要一起來划？」',
      race: '🐉 參加划龍舟',
      zongzi: '竹葉、糯米、香菇和花生。我們來包粽子吧！第一步要做什麼？',
      zongziSteps: [
        { q: '第一步？', a: ['把兩片竹葉折成漏斗形', '綁繩子', '放進去蒸'] },
        { q: '第二步？', a: ['填進糯米和餡料', '吃掉它', '把葉子丟掉'] },
        { q: '第三步？', a: ['把葉子包起來，用繩子綁緊', '放進冰箱', '幫它上色'] },
      ],
      zongziWrong: '嗯，還不是這一步喔。再想想看！',
      zongziDone: '你的粽子放進大蒸籠裡。一個小時後：好好吃！',
      egg: '一個古老的傳統：聽說在端午節正中午的時候，蛋可以立起來喔！',
      tryEgg: '🥚 試試看立蛋',
      eggNoon: '還沒到中午喔——11:00 到 13:00 之間再來！',
      eggWin: '立起來了！大家都在拍手。一整年都會好運喔！',
      eggFail: '搖啊……搖啊……倒下去了。再試一次！',
      bbq: '月亮升起來了，鄰居們在小烤肉架上烤玉米、香腸和魷魚。',
      joinBBQ: '🌽 一起烤肉',
      bbqDone: '你們在滿月下一起分月餅、吃烤玉米。「中秋節快樂！」',
      pomelo: '一籃大大的柚子。小朋友會把柚子皮戴在頭上當好笑的帽子！',
      pomeloHat: '🍈 做一頂柚子帽',
      pomeloDone: '你戴上柚子帽，大家都笑了——柚子又甜又多汁！',
      lion: '鑼鼓和鈸響起來了！一隻金色的獅子跳啊跳，還眨著牠的大眼睛。',
      watchLion: '🦁 看舞獅、幫忙加油',
      lionDone: '獅子向你鞠躬，甩甩鬃毛。這代表好運來了！',
      reunion: '全家人圍著桌子：有魚、水餃、火鍋，還有吃了會長命百歲的長年麵。',
      eatReunion: '🍲 吃年夜飯',
      reunionDone: '爸爸媽媽給你一個紅包：+88 金幣！用英文說：“Happy New Year! Thank you!”',
      reunionAgain: '今年的年夜飯你已經吃過囉。',
    },

    stories: {
      title: '📖 故事時間',
      pick: '選一個故事。點 🔊 可以聽任何一句，或讓我把整個故事唸給你聽。',
      readAll: '▶ 唸給我聽',
      stop: '⏹ 停止',
      gistBtn: '🌐 這個故事在說什麼？',
      quiz: '❓ 問題',
      question: (n: number, total: number) => `第 ${n} 題，共 ${total} 題`,
      right: '✅ 答對了！',
      wrong: '❌ 不太對喔——再看一次故事。',
      result: (score: number, total: number) => `${total} 題裡你答對了 ${score} 題！`,
      again: '🔁 再讀一次',
      list: '📚 所有故事',
      locked: '🔒 去世界的某個地方找到這個故事吧',
      read: '✅ 讀過了',
      gists: {
        kite: '小寶的風箏卡在樹上，一位朋友幫他拿了回來。',
        mochi: '李奶奶的小貓追蝴蝶追到迷路了。',
        market: '莉莉很害羞，但她在夜市用英文點了珍珠奶茶。',
        dragon: '划龍舟比賽是怎麼進行的——團結合作才能贏！',
        grandma: '李奶奶回想她在茶園長大的童年。',
        comic: '一本好笑的漫畫：一隻小烏龜自己做了一枚火箭。',
        tower: '關於台北 101 的小知識：超快的電梯和巨大的金色阻尼球。',
        gorge: '花蓮的太魯閣峽谷：大理石峭壁和土耳其藍的溪水。',
      },
    },

    market: {
      welcome: '🏮 歡迎來到夜市！用英文點餐，老闆會笑咪咪的（有時候還會多送你一點）。',
      look: '只是看看',
      orderPrompt: '要怎麼點餐呢？選出最好的英文句子。',
      wrong: [
        '老闆笑了出來：「哈！說得有禮貌一點吧！」',
        '「什麼？可以再說一次嗎？要客氣一點喔。」',
        '老闆挑了挑眉毛。試試看更友善的說法！',
      ],
      cost: (n: number) => `${n} 金幣`,
      poor: '你的金幣不夠。',
      extra: '「你的英文說得真好！這個多送你。」',
      playFor: (n: number) => `▶ 玩一次（${n} 金幣）`,
      gamesText: '燈光、鈴聲和獎品！玩一次只要幾個金幣。',
      clawText: '一整排亮晶晶的夾娃娃機，裡面塞滿圓滾滾、毛茸茸的娃娃。',
      crowd: [
        '好多香味喔！我都不知道要先吃哪一個。',
        '我最喜歡地瓜球。外面酥酥脆脆，裡面QQ的！',
        '你吃過臭豆腐嗎？聞起來很臭，可是吃起來超好吃。',
        '我在夾娃娃機夾到一隻娃娃了！嗯……夾了十二次才夾到。',
        '別忘了垃圾要自己拿好，丟進垃圾桶喔。',
        '撈金魚的叔叔說，祕訣就是紙網要慢慢移動。',
      ],
      stalls: {
        mkTea: {
          sign: '珍珠奶茶',
          food: 'bubble tea',
          vendor: 'Hi there! What can I get you?',
          ok: 'Could I have a bubble tea with less sugar, please?',
          bad: ['Give me tea.', 'Bubble! Bubble! Now!'],
          done: '甜甜冰冰的，滿滿都是Q彈的珍珠。咕嚕咕嚕！',
          price: 30,
        },
        mkChicken: {
          sign: '炸雞排',
          food: 'fried chicken cutlet',
          vendor: 'Fresh chicken cutlet, bigger than your face! Spicy or not spicy?',
          ok: 'Not spicy, please. Can you cut it for me?',
          bad: ['Chicken. Fast.', 'I want the biggest one for free.'],
          done: '酥脆、多汁，而且超級大！要用兩隻手拿才行！',
          price: 40,
        },
        mkBalls: {
          sign: '地瓜球',
          food: 'sweet potato balls',
          vendor: 'Sweet potato balls, hot and crunchy! Small bag or big bag?',
          ok: 'A small bag, please. Thank you!',
          bad: ['Balls! Give!', 'I don’t like potatoes, give me a bag.'],
          done: '一顆顆膨起來，像金色的小氣球。又脆又Q！',
          price: 20,
        },
        mkTofu: {
          sign: '臭豆腐',
          food: 'stinky tofu',
          vendor: 'Stinky tofu! It smells strong, but it’s delicious. Want to try?',
          ok: 'Sure, I’d like to try it, please!',
          bad: ['Ew, it stinks!', 'Make it not stink.'],
          done: '外酥內嫩，配上台式泡菜。沒想到這麼好吃！',
          price: 35,
        },
        mkOyster: {
          sign: '蚵仔煎',
          food: 'oyster omelette',
          vendor: 'Oyster omelette with sweet sauce! For here or to go?',
          ok: 'For here, please. One oyster omelette.',
          bad: ['Omelette now!', 'I want it to go and to stay.'],
          done: '滿滿的蛋香、軟軟黏黏，還帶一點甜。夜市的經典美食！',
          price: 45,
        },
        mkIce: {
          sign: '芒果冰',
          food: 'mango shaved ice',
          vendor: 'Mango shaved ice — mountains of mango! Would you like condensed milk on top?',
          ok: 'Yes, please! Could I also have an extra spoon for my friend?',
          bad: ['Ice. Mango. Go.', 'No milk, no mango, just give me ice.'],
          done: '綿綿的冰、甜甜的芒果，冰到頭都麻了，好開心！',
          price: 50,
        },
      },
    },
  },
};
